// Form state for a schema-driven step.
//
// Validation timing follows the rule that tested best on long KYC forms: a
// field shows its error only after the user has left it (or after a failed
// submit), but once it *is* showing an error it re-validates on every
// keystroke so the message clears the instant the value becomes valid. That
// avoids both "yelling while I type" and "stale red after I fixed it".

import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { allFields } from "./schema";
import { isBlank } from "./conditions";
import {
  completion,
  firstIncomplete,
  initialValues,
  submitPayload,
  validateField,
  validateSchema,
  visibleFields,
} from "./schema";

/** @typedef {import("./types").FormSchema} FormSchema */

/**
 * @param {Object} config
 * @param {FormSchema} config.schema
 * @param {Record<string, *>} [config.initial]   Known values (resumed case, OCR).
 * @param {(values: Record<string, *>) => void} [config.onChange]
 * @returns {Object} form API consumed by <SchemaForm/> and the step's CTA
 */
export const useSchemaForm = ({ schema, initial, onChange }) => {
  const [values, setValues] = useState(() => initialValues(schema, initial));
  const [touched, setTouched] = useState(/** @type {Record<string, boolean>} */ ({}));
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Re-seed when async data lands (the case record and OCR both arrive after
  // first paint). Only fills fields the user hasn't touched, so a late API
  // response can never overwrite something already typed.
  const initialKey = JSON.stringify(initial || {});
  const touchedRef = useRef(touched);
  touchedRef.current = touched;

  // Each seeded value is applied ONCE. Re-applying it on every later arrival of
  // `initial` would undo work done to the value in the meantime: a reference
  // select resolves document text ("CHEVROLET TAHOE") into the carrier's code,
  // then the case record lands, `initial` is rebuilt with the same raw text and
  // the code is overwritten by the text again. The field had already tried to
  // resolve, so it did not try twice, and an unsubmittable string sat there
  // looking chosen.
  const appliedRef = useRef({ ...(initial || {}) });
  useEffect(() => {
    if (!initial) return;
    /** @type {Record<string, *>} */
    const patch = {};
    for (const [key, value] of Object.entries(initial)) {
      if (value === undefined || value === null || value === "") continue;
      if (touchedRef.current[key]) continue;
      if (appliedRef.current[key] === value) continue; // seeded already
      patch[key] = value;
    }
    if (!Object.keys(patch).length) return;
    appliedRef.current = { ...appliedRef.current, ...patch };
    setValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [key, value] of Object.entries(patch)) {
        if (next[key] === value) continue;
        next[key] = value;
        changed = true;
      }
      return changed ? next : prev;
    });
    // initialKey (not `initial`) so a re-created object with equal content is a no-op
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);

  const { errors: allErrors, isValid } = useMemo(
    () => validateSchema(schema, values),
    [schema, values]
  );

  // Only surface an error the user has earned: field blurred, submit tried —
  // or the value arrived already broken.
  //
  // That last case matters. A value carried in from an earlier step or read off
  // a document was never typed and never blurred, so it would sit there invalid
  // and silent: the CTA looked dead and the progress ring, which counts
  // blankness rather than validity, cheerfully read "all done". "Don't yell
  // while I type" doesn't apply to something the user didn't type.
  //
  // Still untouched is what separates the two: a field the user is *currently*
  // typing into has a value that no longer matches what we seeded it with.
  const errors = useMemo(() => {
    const seeded = (name) =>
      !isBlank(initial?.[name]) && values[name] === initial[name];
    /** @type {Record<string, string>} */
    const shown = {};
    for (const [name, message] of Object.entries(allErrors)) {
      if (submitAttempted || touched[name] || seeded(name)) shown[name] = message;
    }
    return shown;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allErrors, touched, submitAttempted, values, initialKey]);

  // Which fields are filtered by which, derived from each select's
  // `dependsOn`. Changing a parent invalidates its children: a dog's "Mongrel"
  // is not a cat breed, and a Toyota model is not a BMW one. Left alone the
  // stale code stays in the payload and the carrier rejects the quote with
  // something unhelpful.
  const dependents = useMemo(() => {
    /** @type {Record<string, string[]>} */
    const map = {};
    for (const field of allFields(schema)) {
      const source = field.options;
      if (Array.isArray(source) || !source?.dependsOn) continue;
      const parents = Array.isArray(source.dependsOn) ? source.dependsOn : [source.dependsOn];
      for (const parent of parents) {
        (map[parent] ||= []).push(field.name);
      }
    }
    return map;
  }, [schema]);

  const setValue = useCallback(
    (name, value, { keepDependents = false } = {}) => {
      setValues((prev) => {
        if (prev[name] === value) return prev;
        const next = { ...prev, [name]: value };

        // Clear anything that was chosen under the old value, transitively —
        // species → group → breed.
        //
        // Unless this is a normalisation. Resolving a document's "CHEVROLET
        // TAHOE" to the carrier's make code is the same answer written
        // differently, not a new one, so the children must survive it: the
        // model field is holding that same text, waiting for the make to
        // resolve before its own list can load. Clearing it there left the
        // model permanently blank on every mulkiya.
        if (!keepDependents) {
          const queue = [...(dependents[name] || [])];
          while (queue.length) {
            const child = queue.shift();
            if (next[child] === "" || next[child] === undefined) continue;
            next[child] = "";
            queue.push(...(dependents[child] || []));
          }
        }

        if (onChange) onChange(next);
        return next;
      });
    },
    [onChange, dependents]
  );

  // Convenience for the many places that pass an MUI change event straight through.
  const handleChange = useCallback(
    (name) => (eventOrValue) => {
      const value =
        eventOrValue && eventOrValue.target !== undefined
          ? eventOrValue.target.type === "checkbox"
            ? eventOrValue.target.checked
            : eventOrValue.target.value
          : eventOrValue;
      // A field resolving its own value to a canonical code says so, so the
      // change is not mistaken for the user picking something else.
      setValue(name, value, {
        keepDependents: Boolean(eventOrValue?.normalising),
      });
    },
    [setValue]
  );

  const handleBlur = useCallback(
    (name) => () => setTouched((prev) => (prev[name] ? prev : { ...prev, [name]: true })),
    []
  );

  /** Mark everything touched and return whether the form may be submitted. */
  const validateAll = useCallback(() => {
    setSubmitAttempted(true);
    const { isValid: ok } = validateSchema(schema, values);
    if (!ok) {
      setTouched(
        Object.fromEntries(visibleFields(schema, values).map((f) => [f.name, true]))
      );
    }
    return ok;
  }, [schema, values]);

  /** Patch several fields at once — used when OCR results arrive. */
  const patch = useCallback((partial) => {
    setValues((prev) => ({ ...prev, ...partial }));
  }, []);

  const reset = useCallback(() => {
    setValues(initialValues(schema, initial));
    setTouched({});
    setSubmitAttempted(false);
  }, [schema, initial]);

  const progress = useMemo(() => completion(schema, values), [schema, values]);
  const nextIncomplete = useMemo(() => firstIncomplete(schema, values), [schema, values]);

  return {
    values,
    errors,
    /** Name of the first required field still empty — drives "jump to next". */
    nextIncomplete,
    /** Validity ignoring touch state — what the CTA's enabled state keys off. */
    isValid,
    touched,
    progress,
    setValue,
    setValues,
    patch,
    reset,
    handleChange,
    handleBlur,
    validateAll,
    validateField: (name) => {
      const field = visibleFields(schema, values).find((f) => f.name === name);
      return field ? validateField(field, values) : "";
    },
    payload: () => submitPayload(schema, values),
  };
};

export default useSchemaForm;
